import React, { ReactNode, useMemo } from "react";
import { FeatureContext } from "./FeatureContext";
import { useDidUpdate, useMap } from "./hooks";
import { useResetabledState } from "./hooks/useResetableState";
import { SelectedFeature } from "./layer/SelectedFeature";
import { FeatureFromGeojson, useFeaturesStore } from "./FeaturesStore";
import { useFeatureClickSelection } from "./hooks/incontext/useFeatureClickSelection";

/**
 * 피처 선택 관리 props — 반드시 `<FeaturesStore>` 내부에 중첩 사용.
 * 자식에서 `useFeatureStore()` 로 selectedFeature/selectFeature/unSelectFeature 액세스.
 */
export interface SelectedFeatureStoreProps {
  /**
   * @default false
   * @description If you set this property to 'true', you can see selection of annotations.
   */
  isAbledSelection?: boolean;

  children?: ReactNode;

  onSelect?: (value: FeatureFromGeojson | null) => void;

  /**
   * @default true
   * @description If you set this property to 'true', you can see selection of annotations
   */
  isMovable?: boolean;
}

export function SelectedFeatureStore({
  isAbledSelection = false,
  children,
  onSelect,
  isMovable = true,
}: Readonly<SelectedFeatureStoreProps>) {
  const map = useMap();
  const { geoJson, changeGeoJson } = useFeaturesStore();

  const [selectedFeature, selectFeature, unSelectFeature] =
    useResetabledState<FeatureFromGeojson>();

  const select = React.useCallback(
    (value: FeatureFromGeojson | null) => {
      onSelect && onSelect(value);
      !onSelect && selectFeature(value);
    },
    [onSelect, selectFeature]
  );

  useFeatureClickSelection({
    map,
    geoJson,
    selectedFeature,
    onSelect,
    changeGeoJson,
    unSelectFeature,
  });

  useDidUpdate(() => {
    const selected = geoJson.features.find((f) => f.properties?.isSelected);
    if (selected) {
      selectFeature(selected);
    } else {
      unSelectFeature();
    }
  }, [geoJson, selectFeature, unSelectFeature]);

  const providerValue = useMemo(
    () => ({ selectedFeature, selectFeature: select, unSelectFeature }),
    [selectedFeature, select, unSelectFeature]
  );

  return (
    <FeatureContext.Provider value={providerValue}>
      {isAbledSelection && selectedFeature && (
        <SelectedFeature featureGeoJson={selectedFeature} />
      )}
      {children}
    </FeatureContext.Provider>
  );
}
